import Vue from 'vue';

const EventBus = new Vue();

const wildcardListeners = [];
const pendingDelayedEmits = [];
const originalEmit = EventBus.$emit.bind(EventBus);

EventBus.$emit = function (eventName, ...args) {
  originalEmit(eventName, ...args);
  wildcardListeners.forEach(({ pattern, handler }) => {
    if (eventName.match(pattern)) {
      handler(eventName, ...args);
    }
  });
};

/**
 * Emit an event after a specified delay (works exactly like $emit but delayed)
 * @param {string} eventName - Name of the event to emit
 * @param {number} delayMs - Delay in milliseconds before emitting
 * @param {...any} args - Arguments to pass with the event
 * @returns {number} Timeout ID that can be used with $cancelDelayedEmit
 */
EventBus.$emitDelayed = function (eventName, delayMs, ...args) {
  const timeoutId = setTimeout(() => {
    this.$emit(eventName, ...args);

    const index = pendingDelayedEmits.findIndex(item => item.id === timeoutId);
    if (index !== -1) {
      pendingDelayedEmits.splice(index, 1);
    }
  }, delayMs);

  pendingDelayedEmits.push({
    id: timeoutId,
    eventName,
    args
  });

  return timeoutId;
};

/**
 * Subscribe to wildcard event pattern (e.g., "user-*")
 * @param {string} patternStr - wildcard pattern like "user-*"
 * @param {Function} handler - callback
 */
EventBus.$onWildcard = function (patternStr, handler) {
  const regex = patternStr instanceof RegExp ? patternStr : new RegExp('^' + patternStr.replace(/\*/g, '.*') + '$');
  wildcardListeners.push({ pattern: regex, handler });
};

/**
 * Unsubscribe a previously registered wildcard handler
 * @param patternStr
 * @param {Function} handler - same function passed to $onWildcard
 */
EventBus.$offWildcard = function (patternStr, handler) {
  const index = wildcardListeners.findIndex(entry => entry.handler === handler);
  if (index !== -1) {
    wildcardListeners.splice(index, 1);
  }
};

/**
 * Cancel a pending delayed emission
 * @param {number} timeoutId - The ID returned by $emitDelayed
 */
EventBus.$cancelDelayedEmit = function (timeoutId) {
  clearTimeout(timeoutId);
  const index = pendingDelayedEmits.findIndex(item => item.id === timeoutId);
  if (index !== -1) {
    pendingDelayedEmits.splice(index, 1);
  }
};

/**
 * Cancel all pending delayed emissions (optionally filtered by event name)
 * @param {string} [eventName] - If provided, only cancel emissions for this event
 */
EventBus.$cancelAllDelayedEmits = function (eventName) {
  const itemsToCancel = eventName
    ? pendingDelayedEmits.filter(item => item.eventName === eventName)
    : pendingDelayedEmits;

  itemsToCancel.forEach(item => clearTimeout(item.id));

  if (eventName) {
    pendingDelayedEmits = pendingDelayedEmits.filter(item => item.eventName !== eventName);
  } else {
    pendingDelayedEmits = [];
  }
};

export default EventBus;
